import { ApolloClient, FP, gql, RemoteData, Rx, RxOperators } from "../deps"

import * as Environment from "../environment"
import { SellalongClientError } from "../errors"
import {
  CustomerDataQuery,
  CustomerDataQueryVariables,
  CustomerDataByEmailQuery,
  CustomerDataByEmailQueryVariables,
  UpdatePurchaseStateMutation,
  UpdatePurchaseStateMutationVariables,
  RecordEventMutationVariables,
  RecordEventMutation,
  RolloverDiscountMutationVariables,
  RolloverDiscountMutation,
  ViewerCountryQuery,
  ViewerCountryQueryVariables,
} from "./api-types"

export type CustomerDataQueryResponse =
  | CustomerDataQuery["customer"]
  | CustomerDataByEmailQuery["customerByEmail"]

const fragments = {
  purchase: gql`
    fragment PurchaseFragment on Purchase {
      id
      state
      name
      seller
      orderDate
      image

      offer {
        price {
          currency
          value
        }
        pricingDescriptor
      }
    }
  `,

  discount: gql`
    fragment DiscountFragment on PurchaseResaleDiscount {
      id
      claimedOn {
        amountRemaining {
          currency
          value
        }
      }
      creditRolledOver {
        currency
        value
      }
      discountAmount {
        currency
        value
      }
      discountCode
      state
    }
  `,

  customer: gql`
    fragment CustomerFragment on Customer {
      id
      customerId
      globalCustomerId

      purchases(first: 50) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...PurchaseFragment
          }
        }
      }

      discounts(first: 1) {
        edges {
          node {
            ...DiscountFragment
          }
        }
      }
    }
  `,
}

const queries = {
  customerData: gql`
    ${fragments.purchase}
    ${fragments.discount}
    ${fragments.customer}
    query CustomerData($id: ID!) {
      customer(id: $id) {
        ...CustomerFragment
      }
    }
  `,

  customerDataByEmail: gql`
    ${fragments.purchase}
    ${fragments.discount}
    ${fragments.customer}
    query CustomerDataByEmail($email: String!, $storeId: String!) {
      customerByEmail(email: $email, storeId: $storeId) {
        ...CustomerFragment
      }
    }
  `,

  viewerCountry: gql`
    query ViewerCountry {
      viewerCountry
    }
  `,
}

const mutations = {
  recordEvent: gql`
    mutation RecordEvent($event: CustomerEventInput!) {
      recordEvent(event: $event)
    }
  `,

  rolloverDiscount: gql`
    ${fragments.purchase}
    ${fragments.discount}
    ${fragments.customer}
    mutation RolloverDiscount($customerId: ID!, $globalCustomerId: ID!) {
      rolloverDiscountCredit(
        customerId: $customerId
        globalCustomerId: $globalCustomerId
      ) {
        ...CustomerFragment
      }
    }
  `,

  updatePurchaseState: gql`
    ${fragments.purchase}
    mutation UpdatePurchaseState($id: ID!, $state: PurchaseState!) {
      updatePurchaseState(id: $id, state: $state) {
        ...PurchaseFragment
      }
    }
  `,
}

export type GraphQLClient =
  ApolloClient.ApolloClient<ApolloClient.NormalizedCacheObject>

let _client: GraphQLClient | null = null

function getClient(): NonNullable<GraphQLClient> {
  if (_client === null) {
    const httpLink = ApolloClient.createHttpLink({
      uri: Environment.sellalongApiUrl,
      headers: {
        "X-API-Key": Environment.sellalongApiKey,
      },
    })

    _client = new ApolloClient.ApolloClient({
      cache: new ApolloClient.InMemoryCache(),
      link: httpLink,
    })
  }

  return _client
}

const watchQuery: <T, V>(
  options: ApolloClient.WatchQueryOptions<V, T>,
  client?: GraphQLClient
) => Rx.Observable<RemoteData.RemoteData<Error, T>> = FP.function.flow(
  (options, client = getClient()) =>
    Rx.defer(
      FP.function.flow(
        () => client.watchQuery(options),
        <T, V>(observableQuery: ApolloClient.ObservableQuery<T, V>) =>
          new Rx.Observable<ApolloClient.ApolloQueryResult<T>>((observer) =>
            observableQuery.subscribe(observer)
          )
      )
    ),

  RxOperators.map(
    <T>({
      data,
      error,
      errors,
    }: ApolloClient.ApolloQueryResult<T>): RemoteData.RemoteData<Error, T> =>
      error
        ? RemoteData.failure(error)
        : errors && errors.length > 0
        ? RemoteData.failure(
            new ApolloClient.ApolloError({ graphQLErrors: errors })
          )
        : data
        ? RemoteData.success(data)
        : RemoteData.pending
  ),

  RxOperators.catchError(
    FP.function.flow(
      (error) =>
        error instanceof Error
          ? error
          : new Error(`Customer data query error: ${JSON.stringify(error)}`),
      RemoteData.failure,
      Rx.of
    )
  ),

  (observable) => Rx.concat(Rx.of(RemoteData.initial), observable)
)

const toSellalongClientError = (error: Error) =>
  SellalongClientError.from("sellalong-api-unexpected", error)

export const watchCustomerDataById: (
  shopId: string,
  customerId: string,
  client?: GraphQLClient
) => Rx.Observable<
  RemoteData.RemoteData<Error, CustomerDataQuery["customer"]>
> = FP.function.flow(
  (shopId, customerId, client) =>
    Rx.defer(() =>
      watchQuery<CustomerDataQuery, CustomerDataQueryVariables>(
        {
          query: queries.customerData,
          variables: {
            id: `sellalong://shopify/${shopId}/customer/${customerId}`,
          },
          fetchPolicy: "cache-and-network",
          errorPolicy: "all",
        },
        client
      )
    ),

  RxOperators.map(
    RemoteData.bimap(toSellalongClientError, (result) => result.customer)
  )
)

export const watchCustomerDataByEmail: (
  shopId: string,
  email: string,
  client?: GraphQLClient
) => Rx.Observable<
  RemoteData.RemoteData<Error, CustomerDataByEmailQuery["customerByEmail"]>
> = FP.function.flow(
  (shopId, email, client) =>
    Rx.defer(() =>
      watchQuery<CustomerDataByEmailQuery, CustomerDataByEmailQueryVariables>(
        {
          query: queries.customerDataByEmail,
          variables: {
            email,
            storeId: `sellalong://shopify/${shopId}`,
          },
          fetchPolicy: "cache-and-network",
          errorPolicy: "all",
        },
        client
      )
    ),

  RxOperators.map(
    RemoteData.bimap(toSellalongClientError, (result) => result.customerByEmail)
  )
)

export const updateState: (
  variables: UpdatePurchaseStateMutationVariables,
  client?: GraphQLClient
) => FP.task.Task<
  RemoteData.RemoteData<
    Error,
    UpdatePurchaseStateMutation["updatePurchaseState"]
  >
> = FP.function.flow(
  FP.taskEither.tryCatchK(
    (variables, client = getClient()) =>
      client.mutate<
        UpdatePurchaseStateMutation,
        UpdatePurchaseStateMutationVariables
      >({
        mutation: mutations.updatePurchaseState,
        variables,
        errorPolicy: "all",
      }),
    (error) =>
      error instanceof Error
        ? error
        : new Error(
            `Customer update purchase state error: ${JSON.stringify(error)}`
          )
  ),
  FP.task.map(
    FP.function.flow(
      RemoteData.fromEither,
      RemoteData.chain(({ data, errors }) =>
        FP.function.pipe(
          errors,
          FP.option.fromNullable,
          FP.option.filter(FP.readonlyArray.isNonEmpty),
          FP.option.map(
            FP.function.flow(
              (errors) =>
                new ApolloClient.ApolloError({ graphQLErrors: errors }),
              RemoteData.failure
            )
          ),

          FP.option.getOrElse(
            FP.function.flow(
              () => data,
              FP.option.fromNullable,
              FP.option.map((data) => data.updatePurchaseState),
              FP.option.foldW(
                () =>
                  RemoteData.failure(
                    new Error(
                      "No response from Customer update purchase state mutation"
                    )
                  ),
                RemoteData.success
              )
            )
          )
        )
      )
    )
  )
)

export const rolloverDiscount: (
  variables: RolloverDiscountMutationVariables,
  client?: GraphQLClient
) => FP.task.Task<
  RemoteData.RemoteData<
    Error,
    RolloverDiscountMutation["rolloverDiscountCredit"]
  >
> = FP.function.flow(
  FP.taskEither.tryCatchK(
    (variables, client = getClient()) =>
      client.mutate<
        RolloverDiscountMutation,
        RolloverDiscountMutationVariables
      >({
        mutation: mutations.rolloverDiscount,
        variables,
        errorPolicy: "all",
      }),
    (error) =>
      error instanceof Error
        ? error
        : new Error(`Rollover discount error: ${JSON.stringify(error)}`)
  ),
  FP.task.map(
    FP.function.flow(
      RemoteData.fromEither,
      RemoteData.chain(({ data, errors }) =>
        FP.function.pipe(
          errors,
          FP.option.fromNullable,
          FP.option.filter(FP.readonlyArray.isNonEmpty),
          FP.option.map(
            FP.function.flow(
              (errors) =>
                new ApolloClient.ApolloError({ graphQLErrors: errors }),
              RemoteData.failure
            )
          ),

          FP.option.getOrElse(
            FP.function.flow(
              () => data,
              FP.option.fromNullable,
              FP.option.map((data) => data.rolloverDiscountCredit),
              FP.option.foldW(
                () =>
                  RemoteData.failure(
                    new Error("No response from Rollover discount mutation")
                  ),
                RemoteData.success
              )
            )
          )
        )
      )
    )
  )
)

export const recordEvent: (
  event: RecordEventMutationVariables["event"],
  client?: GraphQLClient
) => FP.task.Task<RemoteData.RemoteData<Error, void>> = FP.function.flow(
  FP.taskEither.tryCatchK(
    (event, client = getClient()) =>
      client.mutate<RecordEventMutation, RecordEventMutationVariables>({
        mutation: mutations.recordEvent,
        variables: {
          event,
        },
        errorPolicy: "all",
      }),
    (error) =>
      error instanceof Error
        ? error
        : new Error(
            `Customer update purchase state error: ${JSON.stringify(error)}`
          )
  ),
  FP.task.map(
    FP.function.flow(
      RemoteData.fromEither,
      RemoteData.chain(({ data, errors }) =>
        FP.function.pipe(
          errors,
          FP.option.fromNullable,
          FP.option.filter(FP.readonlyArray.isNonEmpty),
          FP.option.map(
            FP.function.flow(
              (errors) =>
                new ApolloClient.ApolloError({ graphQLErrors: errors }),
              RemoteData.failure
            )
          ),

          FP.option.getOrElse(
            FP.function.flow(
              () => data,
              FP.option.fromNullable,
              FP.option.filter((data) => data.recordEvent),
              FP.option.map(FP.function.constVoid),
              FP.option.foldW(
                () =>
                  RemoteData.failure(
                    new Error(
                      "Unexpected response from Customer record event mutation"
                    )
                  ),
                RemoteData.success
              )
            )
          )
        )
      )
    )
  )
)

export const queryViewerCountry: (
  client?: GraphQLClient
) => FP.task.Task<RemoteData.RemoteData<Error, string>> = FP.function.flow(
  FP.taskEither.tryCatchK(
    (client = getClient()) =>
      client.query<ViewerCountryQuery, ViewerCountryQueryVariables>({
        query: queries.viewerCountry,
        errorPolicy: "all",
      }),
    (error) =>
      error instanceof Error
        ? error
        : new Error(`Viewer country query error: ${JSON.stringify(error)}`)
  ),
  FP.task.map(
    FP.function.flow(
      RemoteData.fromEither,
      RemoteData.chain(({ data, errors }) =>
        FP.function.pipe(
          errors,
          FP.option.fromNullable,
          FP.option.filter(FP.readonlyArray.isNonEmpty),
          FP.option.map(
            FP.function.flow(
              (errors) =>
                new ApolloClient.ApolloError({ graphQLErrors: errors }),
              RemoteData.failure
            )
          ),

          FP.option.getOrElse(
            FP.function.flow(
              () => data,
              FP.option.fromNullable,
              FP.option.chainNullableK((data) => data.viewerCountry),
              FP.option.foldW(
                () =>
                  RemoteData.failure(
                    new Error(
                      "Unexpected or null response from viewer country query"
                    )
                  ),
                RemoteData.success
              )
            )
          )
        )
      )
    )
  )
)
